import EdgeLabel from "Areas/SharedFeatures/edgeLabel";
import GeometryLabel from "Areas/SharedFeatures/geometryLabel";
import NodeLabel from "Areas/SharedFeatures/nodeLabel";
import getId from "Areas/SharedFeatures/types/utils/getId";
import getNodeSource from "Areas/SharedFeatures/types/utils/getNodeSource";
import isDocumentMapDataGeometry from "Areas/SharedFeatures/types/utils/isDocumentMapDataGeometry";
import isEdgeSource from "Areas/SharedFeatures/types/utils/isEdgeSource";
import useKeyboardEventListener from "Common/componentUtils/useKeyboardEventListener";
import Resizable from "Components/resizable";
import Scrollbars from "Components/scrollbars";
import WelcomePanel from "Components/welcomePanel";
import classNames from "classnames";
import debounce from "lodash/debounce";
import filter from "lodash/filter";
import findIndex from "lodash/findIndex";
import forEach from "lodash/forEach";
import head from "lodash/head";
import isEmpty from "lodash/isEmpty";
import some from "lodash/some";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import { v4 as uuid } from "uuid";
import Table from "../table";
import Attributes from "./attributes";
import EdgeAttributes from "./edgeAttributes";
import locale from "./innerPanel.locale";
import { InnerPanelTabs } from "./tabs";
import styles from "./innerPanel.module.css";
import { Button } from "UiKit";
const innerObjectLabelClassName = "line-height-1 v-center-items p-xs";
const buildRowClassName = (id) => `table-item-${id}`;
export default function InnerPanel(props) {
    const [selected, setSelected] = useState(undefined);
    const selectedRef = useRef(undefined);
    const { width, ref: observableRef } = useResizeDetector();
    const [data, setData] = useState([]);
    const gridRef = useRef(null);
    const changeSelectedRowCallbackRef = useRef(debounce(() => {
        var _a;
        const component = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance;
        if (!component)
            return;
        if (selectedRef.current) {
            component.selectRows([selectedRef.current], false).then(() => {
                var _a;
                if ((_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance) {
                    setSelected(selectedRef.current);
                    const selectedRowClassName = selectedRef.current
                        ? buildRowClassName(selectedRef.current)
                        : undefined;
                    const selectedStyledRows = gridRef.current.instance
                        .element()
                        .getElementsByClassName("dx-data-row dx-selection");
                    forEach(selectedStyledRows, row => {
                        if (!selectedRowClassName || !row.classList.contains(selectedRowClassName)) {
                            row.classList.remove("dx-selection");
                        }
                    });
                }
            });
        }
        else {
            component.clearSelection();
            setSelected(undefined);
        }
    }, 100));
    const panelId = useMemo(() => `id${uuid()}`, []);
    useEffect(() => {
        setSelected(prev => props.data && props.data.length > 1 && some(props.data, d => getId(d) === prev)
            ? prev
            : undefined);
        setData(props.data || []);
    }, [props.data]);
    const handleRowClick = useCallback((e) => {
        const { component } = e;
        if (!component || !e.data)
            return;
        const id = getId(e.data);
        const selectedId = head(component.getSelectedRowKeys());
        const newSelectedId = selectedId !== id ? id : undefined;
        selectedRef.current = newSelectedId;
        if (newSelectedId) {
            component.selectRows([newSelectedId], false).then(() => {
                if (!component)
                    return;
                if (newSelectedId !== selectedRef.current)
                    return;
                setSelected(newSelectedId);
            });
        }
        else {
            component.clearSelection();
            setSelected(undefined);
        }
    }, []);
    const rowClassName = useCallback((rowData) => buildRowClassName(getId(rowData) || ""), []);
    const handleKeyDown = useCallback((e) => {
        var _a;
        (_a = e.event) === null || _a === void 0 ? void 0 : _a.preventDefault();
    }, []);
    const handleArrowUpDown = useCallback((event) => {
        var _a;
        const component = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance;
        if (!component)
            return;
        const selectedId = head(component.getSelectedRowKeys());
        if (!selectedId)
            return;
        const selectedIndex = component.getRowIndexByKey(selectedId);
        if (selectedIndex === -1)
            return;
        const newSelectedIndex = event.code === "ArrowDown" ? selectedIndex + 1 : selectedIndex - 1;
        if (newSelectedIndex === -1)
            return;
        const newSelectedId = component.getKeyByRowIndex(newSelectedIndex);
        if (!newSelectedId)
            return;
        selectedRef.current = newSelectedId;
        if (newSelectedId) {
            component.navigateToRow(newSelectedId).then(changeSelectedRowCallbackRef.current);
        }
        else {
            component.clearSelection();
            setSelected(undefined);
        }
    }, []);
    useKeyboardEventListener("ArrowDown", false, handleArrowUpDown);
    useKeyboardEventListener("ArrowUp", false, handleArrowUpDown);
    const table = useMemo(() => data.length > 1 &&
        !isEdgeSource(data[0]) &&
        (!some(data, "DocumentObjectId") || props.documentObjects) ? (React.createElement(Table, { data: data, gridRef: gridRef, documentObjects: props.documentObjects, clearSelectionAfterUpdate: false, onRowClick: handleRowClick, onKeyDown: handleKeyDown, allowColumnResizing: true, columns: props.tableColumns, rowClassName: rowClassName, onRowsPin: props.onRowsPin, zIndex: props.zIndex, wrapperWidth: width || "100%", onOpenGroupTableViewModal: props.onOpenGroupTableViewModal, documentId: props.documentId, isRightSideDetailsPanel: true })) : undefined, [
        data,
        props.documentObjects,
        props.tableColumns,
        handleRowClick,
        handleKeyDown,
        rowClassName,
        props.onRowsPin,
        props.zIndex,
        width
    ]);
    const handleAttributeUpdate = useCallback((id, fieldId, value, fieldType) => {
        if (!props.onDataUpdate)
            return undefined;
        const ids = new Set();
        ids.add(id);
        const updated = head(props.onDataUpdate(ids, fieldId, value, fieldType) || []);
        if (updated) {
            const itemId = getId(updated);
            setData(prev => {
                const index = findIndex(prev, d => getId(d) === itemId);
                if (index === -1)
                    return prev;
                const result = [...prev];
                result[index] = updated;
                return result;
            });
        }
        return updated;
    }, [props.onDataUpdate]);
    const handleDataUpdate = useCallback((ids, fieldId, value) => {
        const updated = (props.onDataUpdate && props.onDataUpdate(ids, fieldId, value)) || [];
        if (!isEmpty(updated)) {
            setData(prev => {
                const result = [...prev];
                forEach(updated, item => {
                    const itemId = getId(item);
                    const index = findIndex(prev, d => getId(d) === itemId);
                    if (index !== -1)
                        result[index] = item;
                });
                return result;
            });
        }
    }, [props.onDataUpdate]);
    const blocksData = useMemo(() => (selected ? filter(data, d => getId(d) === selected) : data), [data, selected]);
    const singleSelectedNodeSource = useMemo(() => (blocksData.length === 1 ? getNodeSource(blocksData[0]) : undefined), [blocksData]);
    const singleSelectedEdgeSource = useMemo(() => (blocksData.length === 1 && isEdgeSource(blocksData[0]) ? blocksData[0] : undefined), [blocksData]);
    const handleGetPreview = useCallback((attributeId, largeAttributeId, completed, rejected) => {
        if (!props.onGetPreview || !props.documentId || !(singleSelectedNodeSource === null || singleSelectedNodeSource === void 0 ? void 0 : singleSelectedNodeSource.NodeId)) {
            rejected && rejected();
            return;
        }
        props.onGetPreview(props.documentId, singleSelectedNodeSource.NodeId, attributeId, largeAttributeId, completed, rejected);
    }, [
        props.onGetPreview,
        props.documentId,
        singleSelectedNodeSource === null || singleSelectedNodeSource === void 0 ? void 0 : singleSelectedNodeSource.NodeId,
        singleSelectedNodeSource === null || singleSelectedNodeSource === void 0 ? void 0 : singleSelectedNodeSource.LargeAttributeValues
    ]);
    const handleUnPinNode = useCallback((nodeSource) => {
        nodeSource.GroupId &&
            !nodeSource.PinnedToGroup &&
            props.onRowsPin &&
            props.onRowsPin([nodeSource], [nodeSource.GroupId], false);
    }, [props.onRowsPin]);
    const blocksComponent = useMemo(() => (React.createElement("div", { className: "grid-rows-1fr-auto h-100-max overflow-hidden" }, (singleSelectedNodeSource || singleSelectedEdgeSource) && (React.createElement("div", { className: "overflow-hidden grid-rows-auto-1fr", key: `${blocksData.length}_${singleSelectedNodeSource === null || singleSelectedNodeSource === void 0 ? void 0 : singleSelectedNodeSource.NodeId}_${singleSelectedEdgeSource === null || singleSelectedEdgeSource === void 0 ? void 0 : singleSelectedEdgeSource.EdgeId}` },
        React.createElement(Scrollbars, { key: (singleSelectedNodeSource === null || singleSelectedNodeSource === void 0 ? void 0 : singleSelectedNodeSource.NodeId) || (singleSelectedEdgeSource === null || singleSelectedEdgeSource === void 0 ? void 0 : singleSelectedEdgeSource.EdgeId) },
            React.createElement("div", { className: "stretched-to-fit" },
                singleSelectedNodeSource && (React.createElement(Attributes, { key: singleSelectedNodeSource.NodeId, className: "p-xs", nodeId: singleSelectedNodeSource.NodeId, documentObjectId: singleSelectedNodeSource.DocumentObjectId, attributeValues: singleSelectedNodeSource.AttributeValues, largeAttributeValues: singleSelectedNodeSource.LargeAttributeValues, documentObjects: props.documentObjects, onDataUpdate: props.canModifyAttributes &&
                        props.onDataUpdate
                        ? handleAttributeUpdate
                        : undefined, zIndex: props.zIndex, onGetPreview: props.onGetPreview ? handleGetPreview : undefined })),
                singleSelectedEdgeSource && (React.createElement(EdgeAttributes, { key: singleSelectedEdgeSource.EdgeId, className: "p-xs", data: singleSelectedEdgeSource })))))))), [
        singleSelectedNodeSource,
        singleSelectedEdgeSource,
        blocksData,
        props.documentObjects,
        props.documentId,
        props.onDataUpdate,
        handleDataUpdate,
        handleAttributeUpdate,
        props.onGetPreview,
        handleGetPreview
    ]);
    const topLabel = useMemo(() => {
        if (singleSelectedNodeSource)
            return (React.createElement("div", null,
                React.createElement(NodeLabel, { imgSize: 44, data: singleSelectedNodeSource, documentObject: props.documentObjects
                        ? props.documentObjects[singleSelectedNodeSource.DocumentObjectId]
                        : undefined, className: innerObjectLabelClassName, zIndex: props.zIndex, onUnPin: props.onRowsPin ? handleUnPinNode : undefined, showDocumentObjectName: true }),
                React.createElement("div", { className: styles["add-in-summary"] },
                    React.createElement(Button, { styleType: "filled", size: "44", width: "100%" },
                        React.createElement("span", null, "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u0441\u0432\u043E\u0434\u043A\u0443"),
                        React.createElement("i", { className: "icon-arrow-up-right" })))));
        if (singleSelectedEdgeSource)
            return (React.createElement(EdgeLabel, { imgSize: 50, data: singleSelectedEdgeSource, className: innerObjectLabelClassName }));
        if (data.length !== 1)
            return null;
        return (isDocumentMapDataGeometry(data[0]) && (React.createElement(GeometryLabel, { imgSize: 50, data: data[0], className: innerObjectLabelClassName })));
    }, [data, singleSelectedNodeSource, singleSelectedEdgeSource]);
    return (React.createElement("div", { className: classNames(styles.container), id: panelId, ref: observableRef },
        data.length === 1 && (React.createElement(React.Fragment, null,
            topLabel,
            React.createElement(InnerPanelTabs, { tabAttribute: blocksComponent, panelId: panelId }))),
        data.length > 1 &&
            (isEdgeSource(data[0]) ? (React.createElement(WelcomePanel, { messageClassName: "font-base", showIcon: false, message: locale.MORE_THAN_ONE_EDGE_SELECTED })) : (React.createElement(Resizable, { className: "custom-bottom-resizable", minHeight: 100, maxHeight: props.height ? props.height - 10 : undefined, defaultHeight: props.height ? props.height / 2 : undefined, handles: "bottom", area: `#${panelId}` }, table)))));
}
